import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded'
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded'

import colors from '@/assets/scss/_themes-vars.module.scss'

import DeviceSelectionDialog, { DeviceProps } from '@/components/ui/dialogs/DeviceSelectionDialog'

import TopbarButton from '../../../layouts/diagnostic/TopbarButton'
import { addDocument } from '@/redux/reducers/document.reducer'
import { useParams } from 'react-router-dom'
import { useTranscribeFromFileMutation } from '@/core/services/transcription'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useSnackbar } from 'notistack'
import { t as tMacro } from '@lingui/macro'
export type DictaphoneProps = {
  defaultDeviceId?: string
  devices: DeviceProps[]
}

export function Dictaphone(props: DictaphoneProps) {
  const { t } = useTranslation()
  const { consultationId } = useParams<{ consultationId: string }>()
  const { defaultDeviceId, devices } = props
  const [isRecording, setIsRecording] = useState(false)
  const [openDeviceSelectionDialog, setOpenDeviceSelectionDialog] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>()
  const chunks = useRef<Blob[]>([])
  const [selectDevice, setSelectDevice] = useState<string>('')
  const [transcribeFile, { isLoading: isTranscribing }] = useTranscribeFromFileMutation()
  const authUser = useAppSelector(selectAuthUser)
  const dispatch = useAppDispatch()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    return () => {
      mediaRecorder?.stream.getTracks().forEach((t) => t.stop())
    }
  }, [mediaRecorder])

  useEffect(() => {
    if (defaultDeviceId) {
      setSelectDevice(defaultDeviceId)
    }
  }, [defaultDeviceId])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRecording || isTranscribing) {
        e.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isRecording, isTranscribing])

  const startRecording = async (deviceId: string) => {
    if (!consultationId) {
      return
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: deviceId,
        noiseSuppression: true,
        echoCancellation: true,
      },
      video: false,
    })

    const recorder = new MediaRecorder(stream)
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.current.push(e.data)
    }

    recorder.onstop = async () => {
      const blob = new Blob(chunks.current, { type: 'audio/webm;codecs=opus' })
      chunks.current = []

      const file = new File([blob], `recording/${new Date().toISOString()}.webm`, { type: blob.type })

      dispatch(
        addDocument({
          documentFile: file,
          objectType: 'consultation',
          objectId: consultationId,
        })
      )

      try {
        await transcribeFile({ file, consultationId: consultationId, userId: authUser?._id! }).unwrap()
        enqueueSnackbar(tMacro`Transcription created successfully`, { variant: 'success', autoHideDuration: 3000 })
      } catch (err) {
        enqueueSnackbar(tMacro`error`, { variant: 'error', autoHideDuration: 3000 })
      }
    }

    recorder.start(100)
    setMediaRecorder(recorder)
    setIsRecording(true)
  }

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop()
      mediaRecorder.stream.getTracks().forEach((track) => track.stop())
      setIsRecording(false)
    }
  }

  const handleClickOnDictaphone = () => {
    if (selectDevice) {
      startRecording(selectDevice)
    } else {
      setOpenDeviceSelectionDialog(true)
    }
  }

  const handleSelectDevice = (deviceId: string) => {
    setSelectDevice(deviceId)
    setOpenDeviceSelectionDialog(false)
    startRecording(deviceId)
  }

  return (
    <>
      {!isRecording ? (
        <Tooltip title={t('layouts.diagnostic.header.startTranscribeButton.tooltip')}>
          <TopbarButton onClick={handleClickOnDictaphone} disabled={isTranscribing}>
            <RecordVoiceOverRoundedIcon htmlColor="#FFF" />
          </TopbarButton>
        </Tooltip>
      ) : (
        <Tooltip title={t('layouts.diagnostic.header.stopTranscribeButton.tooltip')}>
          <TopbarButton onClick={stopRecording} sx={{ position: 'relative' }}>
            <StopCircleRoundedIcon htmlColor="#FFF" />
            <CircularProgress
              disableShrink
              size={24}
              sx={{
                color: colors.red500,
                position: 'absolute',
                zIndex: 1,
              }}
            />
          </TopbarButton>
        </Tooltip>
      )}

      <DeviceSelectionDialog
        open={openDeviceSelectionDialog}
        onClose={() => setOpenDeviceSelectionDialog(false)}
        onSelectDevice={handleSelectDevice}
        showAudioOutput={false}
        showVideoInput={false}
        devices={devices}
        saveChoice={false}
        context="dictaphone"
      />
    </>
  )
}
